import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Minimal per-page SEO helper for the corporate marketing pages
 * (Home, About, Services, Contact, Support). Injects: title,
 * description, canonical, robots, Open Graph and Twitter Card tags.
 *
 * Optionally emits one or more JSON-LD blocks via:
 *   - `schema`  (single object, legacy)
 *   - `schemas` (array of objects, preferred)
 *
 * Pages consume site-level defaults (site name, company info) from
 * the CMS via useSiteSettings — this component only handles the
 * per-page copy.
 */
export default function PageSeo({
  title,
  description,
  path = "/",
  robots = "index,follow",
  image,
  siteName = "Manufacturing and Trading Services",
  schema,
  schemas,
  type = "website",
}) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}${path}`;
  const finalTitle = title
    ? `${title} — ${siteName}`
    : siteName;

  const allSchemas = [
    ...(Array.isArray(schemas) ? schemas : []),
    ...(schema ? [schema] : []),
  ].filter(Boolean);

  return (
    <Helmet>
      <title>{finalTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      <meta name="robots" content={robots} />

      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />
      {image && <meta property="og:image" content={image} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={finalTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}

      {/* Optional JSON-LD structured data */}
      {allSchemas.map((s, i) => (
        <script
          key={`page-schema-${s?.["@type"] || i}`}
          type="application/ld+json"
        >
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
